<template>
  <div class="questionnaire-analyze-container">
    <!-- 页面标题 -->
    <el-card class="page-header-card">
      <div class="page-header">
        <div class="header-left">
          <h2>问卷数据分析</h2>
          <el-tag type="primary" v-if="userRole === 'admin'">管理员工作台</el-tag>
          <el-tag type="success" v-else-if="userRole === 'manager'">项目管理师工作台</el-tag>
        </div>
        <div class="header-right">
          <el-button type="primary" icon="el-icon-download" @click="exportData">数据导出</el-button>
          <el-button icon="el-icon-refresh" @click="refreshData">刷新数据</el-button>
        </div>
      </div>
    </el-card>

    <!-- 搜索筛选区 -->
    <el-card class="search-card">
      <el-form :inline="true" :model="searchForm" ref="searchForm" size="small">
        <el-form-item label="问卷类型">
          <el-select v-model="searchForm.questionnaireType" placeholder="全部类型" clearable>
            <el-option label="满意度问卷" value="satisfaction"></el-option>
            <el-option label="随访问卷" value="followup"></el-option>
            <el-option label="评估问卷" value="assessment"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="科室">
          <el-select v-model="searchForm.department" placeholder="全部科室" clearable>
            <el-option v-for="dept in departmentOptions" :key="dept.value" :label="dept.label" :value="dept.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="提交时间">
          <el-date-picker
            v-model="searchForm.timeRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            clearable>
          </el-date-picker>
        </el-form-item>
        <el-form-item label="时间粒度">
          <el-select v-model="searchForm.timePeriod" placeholder="时间粒度">
            <el-option label="日" value="day"></el-option>
            <el-option label="周" value="week"></el-option>
            <el-option label="月" value="month"></el-option>
            <el-option label="季度" value="quarter"></el-option>
            <el-option label="年" value="year"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据概览 -->
    <el-row :gutter="20" class="data-overview">
      <el-col :span="6">
        <el-card class="overview-card">
          <div class="overview-item">
            <div class="overview-icon blue">
              <i class="el-icon-document"></i>
            </div>
            <div class="overview-content">
              <div class="overview-title">问卷总数</div>
              <div class="overview-value">{{ dashboardData.totalQuestionnaires }}</div>
              <div class="overview-trend">
                <span :class="dashboardData.questionnaireTrend >= 0 ? 'up' : 'down'">
                  <i :class="dashboardData.questionnaireTrend >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
                  {{ Math.abs(dashboardData.questionnaireTrend) }}%
                </span>
                较上期
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="overview-card">
          <div class="overview-item">
            <div class="overview-icon green">
              <i class="el-icon-finished"></i>
            </div>
            <div class="overview-content">
              <div class="overview-title">完成率</div>
              <div class="overview-value">{{ dashboardData.completionRate }}%</div>
              <div class="overview-trend">
                <span :class="dashboardData.completionTrend >= 0 ? 'up' : 'down'">
                  <i :class="dashboardData.completionTrend >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
                  {{ Math.abs(dashboardData.completionTrend) }}%
                </span>
                较上期
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="overview-card">
          <div class="overview-item">
            <div class="overview-icon orange">
              <i class="el-icon-star-on"></i>
            </div>
            <div class="overview-content">
              <div class="overview-title">平均满意度</div>
              <div class="overview-value">{{ dashboardData.averageSatisfaction }}</div>
              <div class="overview-trend">
                <span :class="dashboardData.satisfactionTrend >= 0 ? 'up' : 'down'">
                  <i :class="dashboardData.satisfactionTrend >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
                  {{ Math.abs(dashboardData.satisfactionTrend).toFixed(1) }}
                </span>
                较上期
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="overview-card">
          <div class="overview-item">
            <div class="overview-icon red">
              <i class="el-icon-warning"></i>
            </div>
            <div class="overview-content">
              <div class="overview-title">异常标记率</div>
              <div class="overview-value">{{ dashboardData.abnormalRate }}%</div>
              <div class="overview-trend">
                <span :class="dashboardData.abnormalTrend <= 0 ? 'up' : 'down'">
                  <i :class="dashboardData.abnormalTrend <= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
                  {{ Math.abs(dashboardData.abnormalTrend) }}%
                </span>
                较上期
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 图表展示区域 -->
    <el-row :gutter="20">
      <!-- 问卷提交趋势 -->
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span>问卷提交趋势</span>
            <el-radio-group v-model="submissionChartType" size="mini" @change="updateSubmissionChart">
              <el-radio-button label="all">所有问卷</el-radio-button>
              <el-radio-button label="type">按类型</el-radio-button>
            </el-radio-group>
          </div>
          <div class="chart-container" id="submissionTrendChart"></div>
        </el-card>
      </el-col>

      <!-- 满意度评分分布 -->
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span>满意度评分分布</span>
            <el-dropdown @command="handleSatisfactionViewChange">
              <span class="el-dropdown-link">
                {{ satisfactionViewText }}<i class="el-icon-arrow-down el-icon--right"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="pie">饼图视图</el-dropdown-item>
                <el-dropdown-item command="bar">柱状图视图</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
          <div class="chart-container" id="satisfactionDistChart"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" style="margin-top: 20px;">
      <!-- 评分量表趋势分析 -->
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span>评分量表趋势分析</span>
            <el-select v-model="selectedScale" placeholder="选择评估量表" size="mini" @change="updateScaleTrendChart">
              <el-option v-for="scale in scaleOptions" :key="scale.value" :label="scale.label" :value="scale.value"></el-option>
            </el-select>
          </div>
          <div class="chart-container" id="scaleTrendChart"></div>
        </el-card>
      </el-col>

      <!-- 科室填写情况对比 -->
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span>科室填写情况对比</span>
            <el-radio-group v-model="deptCompareMetric" size="mini" @change="updateDeptCompareChart">
              <el-radio-button label="count">提交数量</el-radio-button>
              <el-radio-button label="completion">完成率</el-radio-button>
              <el-radio-button label="satisfaction">满意度</el-radio-button>
            </el-radio-group>
          </div>
          <div class="chart-container" id="deptCompareChart"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" style="margin-top: 20px;">
      <!-- 问题回答分析 -->
      <el-col :span="24">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span>问题回答分析</span>
            <div class="header-controls">
              <el-select v-model="selectedQuestionnaire" placeholder="选择问卷" size="mini" style="width: 200px; margin-right: 10px;" @change="handleQuestionnaireChange">
                <el-option v-for="item in questionnaireOptions" :key="item.id" :label="item.name" :value="item.id"></el-option>
              </el-select>
              <el-select v-model="selectedQuestion" placeholder="选择问题" size="mini" style="width: 200px;" @change="updateQuestionAnswerChart">
                <el-option v-for="item in questionOptions" :key="item.id" :label="item.title" :value="item.id"></el-option>
              </el-select>
            </div>
          </div>
          <div class="chart-container" id="questionAnswerChart"></div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 数据表格区域 -->
    <el-card class="table-card" style="margin-top: 20px;">
      <div slot="header" class="card-header">
        <span>问卷数据明细</span>
        <div class="header-actions">
          <el-button type="text" icon="el-icon-download" @click="exportTableData">导出表格数据</el-button>
        </div>
      </div>
      
      <el-table :data="tableData" border v-loading="loading" style="width: 100%">
        <el-table-column type="index" width="50" label="序号"></el-table-column>
        <el-table-column prop="questionnaireName" label="问卷名称" min-width="180"></el-table-column>
        <el-table-column prop="questionnaireType" label="问卷类型" width="120">
          <template slot-scope="scope">
            <el-tag :type="getQuestionnaireTypeTag(scope.row.questionnaireType)">
              {{ getQuestionnaireTypeText(scope.row.questionnaireType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="department" label="科室" width="120"></el-table-column>
        <el-table-column prop="totalCount" label="发送数量" width="100"></el-table-column>
        <el-table-column prop="completedCount" label="完成数量" width="100"></el-table-column>
        <el-table-column prop="completionRate" label="完成率" width="100">
          <template slot-scope="scope">
            {{ scope.row.completionRate }}%
          </template>
        </el-table-column>
        <el-table-column prop="avgSatisfaction" label="平均满意度" width="120">
          <template slot-scope="scope">
            <el-rate v-if="scope.row.questionnaireType === 'satisfaction'" v-model="scope.row.avgSatisfaction" disabled show-score text-color="#ff9900"></el-rate>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="abnormalRate" label="异常率" width="100">
          <template slot-scope="scope">
            <span :class="scope.row.abnormalRate > 20 ? 'text-danger' : scope.row.abnormalRate > 10 ? 'text-warning' : ''">
              {{ scope.row.abnormalRate }}%
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="period" label="统计周期" width="180"></el-table-column>
        <el-table-column label="操作" width="120">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="viewDetails(scope.row)">详情</el-button>
            <el-button type="text" size="small" @click="analyzeDetail(scope.row)">分析</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </el-card>

    <!-- 导出数据对话框 -->
    <el-dialog
      title="数据导出"
      :visible.sync="exportDialogVisible"
      width="500px"
    >
      <el-form :model="exportForm" label-width="120px">
        <el-form-item label="导出内容">
          <el-checkbox-group v-model="exportForm.content">
            <el-checkbox label="summary">数据概览</el-checkbox>
            <el-checkbox label="charts">图表数据</el-checkbox>
            <el-checkbox label="details">明细数据</el-checkbox>
            <el-checkbox label="raw">原始问卷数据</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="文件格式">
          <el-radio-group v-model="exportForm.format">
            <el-radio label="excel">Excel (.xlsx)</el-radio>
            <el-radio label="pdf">PDF (.pdf)</el-radio>
            <el-radio label="csv">CSV (.csv)</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="是否包含图表">
          <el-switch v-model="exportForm.includeCharts"></el-switch>
        </el-form-item>
        <el-form-item label="是否匿名化">
          <el-switch v-model="exportForm.anonymize"></el-switch>
          <div class="form-tip">匿名化处理将移除所有可识别个人信息</div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="exportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmExport">确认导出</el-button>
      </span>
    </el-dialog>

    <!-- 问卷详细分析对话框 -->
    <el-dialog
      title="问卷详细分析"
      :visible.sync="detailAnalysisVisible"
      width="80%"
    >
      <div v-if="currentAnalysis" class="detail-analysis">
        <el-tabs v-model="analysisActiveTab">
          <el-tab-pane label="统计概览" name="overview">
            <div class="analysis-overview">
              <el-descriptions :column="2" border>
                <el-descriptions-item label="问卷名称">{{ currentAnalysis.questionnaireName }}</el-descriptions-item>
                <el-descriptions-item label="问卷类型">{{ getQuestionnaireTypeText(currentAnalysis.questionnaireType) }}</el-descriptions-item>
                <el-descriptions-item label="统计周期">{{ currentAnalysis.period }}</el-descriptions-item>
                <el-descriptions-item label="所属科室">{{ currentAnalysis.department }}</el-descriptions-item>
                <el-descriptions-item label="发送数量">{{ currentAnalysis.totalCount }}</el-descriptions-item>
                <el-descriptions-item label="完成数量">{{ currentAnalysis.completedCount }}</el-descriptions-item>
                <el-descriptions-item label="完成率">{{ currentAnalysis.completionRate }}%</el-descriptions-item>
                <el-descriptions-item label="平均满意度">
                  <el-rate v-if="currentAnalysis.questionnaireType === 'satisfaction'" v-model="currentAnalysis.avgSatisfaction" disabled show-score text-color="#ff9900"></el-rate>
                  <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item label="异常标记率">{{ currentAnalysis.abnormalRate }}%</el-descriptions-item>
                <el-descriptions-item label="平均填写时长">{{ currentAnalysis.avgDuration || '-' }}</el-descriptions-item>
              </el-descriptions>

              <div class="analysis-indicators">
                <h4>关键指标分析</h4>
                <div class="indicator-items">
                  <div class="indicator-item" v-for="(item, index) in currentAnalysis.keyIndicators" :key="index">
                    <div class="indicator-title">{{ item.name }}</div>
                    <el-progress :percentage="item.value" :color="getProgressColor(item.value)" :format="format => `${item.value}%`"></el-progress>
                    <div class="indicator-note">{{ item.note }}</div>
                  </div>
                </div>
              </div>
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="问题分析" name="questions">
            <div class="question-analysis" v-for="(question, index) in currentAnalysis.questions" :key="index">
              <div class="question-header">
                <div class="question-title">问题 {{ index + 1 }}: {{ question.title }}</div>
                <div class="question-meta">
                  <span>类型: {{ question.type }}</span>
                  <span>回答率: {{ question.answerRate }}%</span>
                  <span v-if="question.abnormalRate">异常率: {{ question.abnormalRate }}%</span>
                </div>
              </div>
              
              <div class="question-chart" :id="`questionChart-${index}`"></div>
              
              <div class="question-insights">
                <h5>分析洞察</h5>
                <p>{{ question.insight }}</p>
              </div>
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="时间趋势" name="trend">
            <div class="trend-analysis">
              <div class="trend-chart" id="detailTrendChart"></div>
              <div class="trend-insights">
                <h5>趋势分析</h5>
                <p>{{ currentAnalysis.trendInsight }}</p>
              </div>
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="分群比较" name="groups" v-if="userRole === 'admin'">
            <div class="group-analysis">
              <el-row :gutter="20">
                <el-col :span="6">
                  <div class="filter-panel">
                    <h5>分群依据</h5>
                    <el-radio-group v-model="groupByFactor" @change="updateGroupAnalysis">
                      <el-radio-button label="gender">性别</el-radio-button>
                      <el-radio-button label="age">年龄段</el-radio-button>
                      <el-radio-button label="disease">疾病类型</el-radio-button>
                      <el-radio-button label="area">地区</el-radio-button>
                    </el-radio-group>
                  </div>
                </el-col>
                <el-col :span="18">
                  <div class="group-chart" id="groupAnalysisChart"></div>
                </el-col>
              </el-row>
              
              <div class="group-insights">
                <h5>分群洞察</h5>
                <p>{{ currentAnalysis.groupInsight }}</p>
              </div>
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="AI分析" name="ai">
            <div class="ai-analysis">
              <el-alert
                title="AI分析结果仅供参考，关键决策请结合专业判断"
                type="info"
                show-icon
                :closable="false">
              </el-alert>
              
              <div class="ai-summary">
                <h4>AI概要分析</h4>
                <p>{{ currentAnalysis.aiSummary }}</p>
              </div>
              
              <div class="ai-insights">
                <h4>关键洞察</h4>
                <el-collapse v-model="activeAIInsights">
                  <el-collapse-item :title="insight.title" :name="index" v-for="(insight, index) in currentAnalysis.aiInsights" :key="index">
                    <div>{{ insight.content }}</div>
                  </el-collapse-item>
                </el-collapse>
              </div>
              
              <div class="ai-recommendations">
                <h4>AI建议</h4>
                <el-timeline>
                  <el-timeline-item
                    v-for="(recommendation, index) in currentAnalysis.aiRecommendations"
                    :key="index"
                    :type="recommendation.type"
                    :color="getRecommendationColor(recommendation.priority)"
                    :timestamp="getPriorityText(recommendation.priority)">
                    {{ recommendation.content }}
                  </el-timeline-item>
                </el-timeline>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="exportAnalysisReport" type="success">导出分析报告</el-button>
        <el-button type="primary" @click="detailAnalysisVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: "QuestionnaireAnalyze",
  data() {
    return {
      // 用户角色
      userRole: 'admin', // 可以是 'admin' 或 'manager'
      
      // 搜索表单
      searchForm: {
        questionnaireType: '',
        department: '',
        timeRange: [],
        timePeriod: 'month'
      },
      
      // 部门选项
      departmentOptions: [
        { value: 'cardiology', label: '心内科' },
        { value: 'neurology', label: '神经内科' },
        { value: 'respiratory', label: '呼吸内科' },
        { value: 'gastroenterology', label: '消化内科' },
        { value: 'nephrology', label: '肾内科' },
        { value: 'endocrinology', label: '内分泌科' },
        { value: 'oncology', label: '肿瘤科' },
        { value: 'general', label: '普通外科' },
        { value: 'orthopedics', label: '骨科' },
        { value: 'all', label: '全院' }
      ],
      
      // 仪表板数据
      dashboardData: {
        totalQuestionnaires: 1268,
        questionnaireTrend: 12.5,
        completionRate: 85.6,
        completionTrend: 3.2,
        averageSatisfaction: 4.6,
        satisfactionTrend: 0.2,
        abnormalRate: 7.8,
        abnormalTrend: -2.1
      },
      
      // 图表控制
      submissionChartType: 'all',
      satisfactionView: 'pie',
      satisfactionViewText: '饼图视图',
      selectedScale: 'life-quality',
      deptCompareMetric: 'count',
      selectedQuestionnaire: '',
      selectedQuestion: '',
      
      // 评估量表选项
      scaleOptions: [
        { value: 'life-quality', label: '生活质量评估表' },
        { value: 'sf-36', label: 'SF-36健康量表' },
        { value: 'phq-9', label: '抑郁筛查量表(PHQ-9)' },
        { value: 'eq-5d', label: '健康状态量表(EQ-5D)' },
        { value: 'pain-scale', label: '疼痛评估量表' }
      ],
      
      // 问卷选项
      questionnaireOptions: [],
      questionOptions: [],
      
      // 数据表格
      loading: false,
      tableData: [],
      
      // 分页控制
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      
      // 导出对话框
      exportDialogVisible: false,
      exportForm: {
        content: ['summary', 'charts', 'details'],
        format: 'excel',
        includeCharts: true,
        anonymize: true
      },
      
      // 问卷详细分析
      detailAnalysisVisible: false,
      analysisActiveTab: 'overview',
      currentAnalysis: null,
      groupByFactor: 'age',
      activeAIInsights: [0, 1]
    };
  },
  
  mounted() {
    this.fetchData();
    this.$nextTick(() => {
      this.initCharts();
    });
    
    // 添加窗口大小变化时图表自动调整大小
    window.addEventListener('resize', this.resizeAllCharts);
  },
  
  beforeDestroy() {
    // 销毁图表实例，避免内存泄漏
    window.removeEventListener('resize', this.resizeAllCharts);
    this.disposeAllCharts();
  },
  
  methods: {
    // 初始化所有图表
    initCharts() {
      this.initSubmissionTrendChart();
      this.initSatisfactionDistChart();
      this.initScaleTrendChart();
      this.initDeptCompareChart();
      this.initQuestionAnswerChart();
    },
    
    // 获取数据
    fetchData() {
      this.loading = true;
      
      // 模拟异步请求
      setTimeout(() => {
        // 获取问卷选项
        this.questionnaireOptions = [
          { id: 'q1', name: '患者满意度调查问卷' },
          { id: 'q2', name: '术后康复随访问卷' },
          { id: 'q3', name: '慢性病管理评估表' },
          { id: 'q4', name: '门诊就医体验调查' },
          { id: 'q5', name: '生活质量评估量表' }
        ];
        
        // 生成表格数据
        this.tableData = [
          {
            id: '1',
            questionnaireName: '患者满意度调查问卷',
            questionnaireType: 'satisfaction',
            department: '全院',
            totalCount: 500,
            completedCount: 432,
            completionRate: 86.4,
            avgSatisfaction: 4.7,
            abnormalRate: 5.3,
            period: '2023年1月-12月'
          },
          {
            id: '2',
            questionnaireName: '术后康复随访问卷',
            questionnaireType: 'followup',
            department: '普通外科',
            totalCount: 320,
            completedCount: 268,
            completionRate: 83.8,
            avgSatisfaction: 0,
            abnormalRate: 8.2,
            period: '2023年1月-12月'
          },
          {
            id: '3',
            questionnaireName: '糖尿病管理评估表',
            questionnaireType: 'assessment',
            department: '内分泌科',
            totalCount: 180,
            completedCount: 162,
            completionRate: 90.0,
            avgSatisfaction: 0,
            abnormalRate: 12.5,
            period: '2023年1月-12月'
          },
          {
            id: '4',
            questionnaireName: '门诊就医体验调查',
            questionnaireType: 'satisfaction',
            department: '全院',
            totalCount: 850,
            completedCount: 693,
            completionRate: 81.5,
            avgSatisfaction: 4.3,
            abnormalRate: 7.6,
            period: '2023年1月-12月'
          },
          {
            id: '5',
            questionnaireName: '生活质量评估量表',
            questionnaireType: 'assessment',
            department: '肿瘤科',
            totalCount: 230,
            completedCount: 204,
            completionRate: 88.7,
            avgSatisfaction: 0,
            abnormalRate: 15.2,
            period: '2023年1月-12月'
          }
        ];
        
        this.pagination.total = this.tableData.length;
        this.loading = false;
      }, 500);
    },
    
    // 问卷提交趋势图表
    initSubmissionTrendChart() {
      const chartDom = document.getElementById('submissionTrendChart');
      if (!chartDom) return;
      
      const chart = echarts.init(chartDom);
      
      let option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: this.submissionChartType === 'all' ? 
            ['问卷提交数量'] : 
            ['满意度问卷', '随访问卷', '评估问卷']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
        },
        yAxis: {
          type: 'value',
          name: '提交数量'
        },
        series: this.submissionChartType === 'all' ? 
          [
            {
              name: '问卷提交数量',
              type: 'line',
              smooth: true,
              data: [120, 132, 101, 134, 90, 230, 210, 182, 191, 234, 260, 330],
              areaStyle: {
                opacity: 0.3
              },
              lineStyle: {
                width: 3
              },
              itemStyle: {
                color: '#409EFF'
              }
            }
          ] : 
          [
            {
              name: '满意度问卷',
              type: 'line',
              stack: 'Total',
              smooth: true,
              data: [50, 60, 45, 65, 35, 85, 70, 62, 71, 84, 90, 120],
              areaStyle: {
                opacity: 0.3
              },
              itemStyle: {
                color: '#67C23A'
              }
            },
            {
              name: '随访问卷',
              type: 'line',
              stack: 'Total',
              smooth: true,
              data: [40, 42, 31, 44, 30, 80, 90, 70, 65, 90, 100, 110],
              areaStyle: {
                opacity: 0.3
              },
              itemStyle: {
                color: '#409EFF'
              }
            },
            {
              name: '评估问卷',
              type: 'line',
              stack: 'Total',
              smooth: true,
              data: [30, 30, 25, 25, 25, 65, 50, 50, 55, 60, 70, 100],
              areaStyle: {
                opacity: 0.3
              },
              itemStyle: {
                color: '#E6A23C'
              }
            }
          ]
      };
      
      chart.setOption(option);
      this.submissionTrendChart = chart;
    },
    
    // 满意度评分分布图表
    initSatisfactionDistChart() {
      const chartDom = document.getElementById('satisfactionDistChart');
      if (!chartDom) return;
      
      const chart = echarts.init(chartDom);
      
      let option = {};
      
      if (this.satisfactionView === 'pie') {
        option = {
          tooltip: {
            trigger: 'item',
            formatter: '{a} <br/>{b}: {c} ({d}%)'
          },
          legend: {
            orient: 'vertical',
            left: 10,
            data: ['非常满意', '满意', '一般', '不满意', '非常不满意']
          },
          series: [
            {
              name: '满意度分布',
              type: 'pie',
              radius: ['50%', '70%'],
              avoidLabelOverlap: false,
              label: {
                show: true,
                position: 'outside',
                formatter: '{b}: {d}%'
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: '16',
                  fontWeight: 'bold'
                }
              },
              labelLine: {
                show: true
              },
              data: [
                { value: 55, name: '非常满意', itemStyle: { color: '#67C23A' } },
                { value: 32, name: '满意', itemStyle: { color: '#85ce61' } },
                { value: 10, name: '一般', itemStyle: { color: '#E6A23C' } },
                { value: 2, name: '不满意', itemStyle: { color: '#f89b9b' } },
                { value: 1, name: '非常不满意', itemStyle: { color: '#F56C6C' } }
              ]
            }
          ]
        };
      } else {
        option = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            }
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: ['非常满意', '满意', '一般', '不满意', '非常不满意']
          },
          yAxis: {
            type: 'value',
            name: '百分比 (%)'
          },
          series: [
            {
              name: '满意度分布',
              type: 'bar',
              data: [
                { value: 55, itemStyle: { color: '#67C23A' } },
                { value: 32, itemStyle: { color: '#85ce61' } },
                { value: 10, itemStyle: { color: '#E6A23C' } },
                { value: 2, itemStyle: { color: '#f89b9b' } },
                { value: 1, itemStyle: { color: '#F56C6C' } }
              ],
              label: {
                show: true,
                position: 'top',
                formatter: '{c}%'
              }
            }
          ]
        };
      }
      
      chart.setOption(option);
      this.satisfactionDistChart = chart;
    },
    
    // 评分量表趋势分析图表
    initScaleTrendChart() {
      const chartDom = document.getElementById('scaleTrendChart');
      if (!chartDom) return;
      
      const chart = echarts.init(chartDom);
      
      const option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['平均得分', '正常参考值']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
        },
        yAxis: {
          type: 'value',
          name: '得分',
          min: 0,
          max: 100
        },
        series: [
          {
            name: '平均得分',
            type: 'line',
            data: [65, 68, 70, 72, 75, 76, 78, 80, 79, 82, 83, 85],
            lineStyle: {
              width: 3
            },
            itemStyle: {
              color: '#409EFF'
            },
            markLine: {
              data: [
                { 
                  name: '平均值',
                  type: 'average',
                  label: {
                    position: 'middle',
                    formatter: '平均值: {c}'
                  }
                }
              ],
              symbol: ['none', 'none'],
              lineStyle: {
                type: 'solid',
                color: '#409EFF'
              }
            }
          },
          {
            name: '正常参考值',
            type: 'line',
            data: [80, 80, 80, 80, 80, 80, 80, 80, 80, 80, 80, 80],
            lineStyle: {
              width: 2,
              type: 'dashed'
            },
            itemStyle: {
              color: '#E6A23C'
            }
          }
        ]
      };
      
      chart.setOption(option);
      this.scaleTrendChart = chart;
    },
    
    // 科室填写情况对比图表
    initDeptCompareChart() {
      const chartDom = document.getElementById('deptCompareChart');
      if (!chartDom) return;
      
      const chart = echarts.init(chartDom);
      
      let data = [];
      let name = '';
      
      if (this.deptCompareMetric === 'count') {
        data = [
          { value: 320, name: '心内科' },
          { value: 240, name: '肿瘤科' },
          { value: 280, name: '神经内科' },
          { value: 180, name: '消化内科' },
          { value: 150, name: '呼吸内科' },
          { value: 220, name: '骨科' },
          { value: 190, name: '内分泌科' }
        ];
        name = '提交数量';
      } else if (this.deptCompareMetric === 'completion') {
        data = [
          { value: 92, name: '心内科' },
          { value: 85, name: '肿瘤科' },
          { value: 88, name: '神经内科' },
          { value: 78, name: '消化内科' },
          { value: 81, name: '呼吸内科' },
          { value: 90, name: '骨科' },
          { value: 87, name: '内分泌科' }
        ];
        name = '完成率(%)';
      } else {
        data = [
          { value: 4.8, name: '心内科' },
          { value: 4.5, name: '肿瘤科' },
          { value: 4.6, name: '神经内科' },
          { value: 4.3, name: '消化内科' },
          { value: 4.4, name: '呼吸内科' },
          { value: 4.7, name: '骨科' },
          { value: 4.5, name: '内分泌科' }
        ];
        name = '平均满意度';
      }
      
      // 数据排序
      data.sort((a, b) => b.value - a.value);
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          name: name,
          axisLabel: {
            formatter: '{value}'
          }
        },
        yAxis: {
          type: 'category',
          data: data.map(item => item.name),
          axisLine: {
            lineStyle: {
              color: '#333'
            }
          },
          axisTick: {
            show: false
          }
        },
        series: [
          {
            name: name,
            type: 'bar',
            data: data.map(item => {
              return {
                value: item.value,
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                    { offset: 0, color: '#83bff6' },
                    { offset: 0.5, color: '#409EFF' },
                    { offset: 1, color: '#188df0' }
                  ])
                }
              }
            }),
            label: {
              show: true,
              position: 'right',
              formatter: '{c}'
            }
          }
        ]
      };
      
      chart.setOption(option);
      this.deptCompareChart = chart;
    },
    
    // 问题回答分析图表
    initQuestionAnswerChart() {
      const chartDom = document.getElementById('questionAnswerChart');
      if (!chartDom) return;
      
      const chart = echarts.init(chartDom);
      
      // 模拟问题回答数据
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'horizontal',
          bottom: 10,
          data: ['非常满意', '满意', '一般', '不满意', '非常不满意']
        },
        series: [
          {
            name: '问题回答分布',
            type: 'pie',
            radius: '50%',
            center: ['50%', '45%'],
            data: [
              { value: 60, name: '非常满意', itemStyle: { color: '#67C23A' } },
              { value: 30, name: '满意', itemStyle: { color: '#85ce61' } },
              { value: 8, name: '一般', itemStyle: { color: '#E6A23C' } },
              { value: 1.5, name: '不满意', itemStyle: { color: '#f89b9b' } },
              { value: 0.5, name: '非常不满意', itemStyle: { color: '#F56C6C' } }
            ],
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            label: {
              formatter: '{b}: {d}%'
            }
          }
        ]
      };
      
      chart.setOption(option);
      this.questionAnswerChart = chart;
    },
    
    // 更新问卷提交趋势图表
    updateSubmissionChart() {
      this.initSubmissionTrendChart();
    },
    
    // 更新满意度评分分布图表
    handleSatisfactionViewChange(view) {
      this.satisfactionView = view;
      this.satisfactionViewText = view === 'pie' ? '饼图视图' : '柱状图视图';
      this.initSatisfactionDistChart();
    },
    
    // 更新评分量表趋势分析图表
    updateScaleTrendChart() {
      // 根据选择的量表更新图表数据
      if (this.scaleTrendChart) {
        // 这里只是示例，实际应该根据选择的量表获取相应的数据
        const data = {
          'life-quality': [65, 68, 70, 72, 75, 76, 78, 80, 79, 82, 83, 85],
          'sf-36': [70, 72, 74, 75, 77, 76, 78, 80, 82, 84, 85, 86],
          'phq-9': [8, 7, 6, 5, 6, 5, 4, 4, 5, 4, 3, 3],
          'eq-5d': [0.75, 0.77, 0.78, 0.80, 0.82, 0.83, 0.85, 0.86, 0.85, 0.87, 0.88, 0.89],
          'pain-scale': [5, 4, 4, 3, 3, 3, 2, 2, 2, 1, 1, 1]
        };
        
        const refValue = {
          'life-quality': 80,
          'sf-36': 85,
          'phq-9': 5,
          'eq-5d': 0.85,
          'pain-scale': 3
        };
        
        const yMax = {
          'life-quality': 100,
          'sf-36': 100,
          'phq-9': 27,
          'eq-5d': 1,
          'pain-scale': 10
        };
        
        const yName = {
          'life-quality': '得分',
          'sf-36': '得分',
          'phq-9': '得分（越低越好）',
          'eq-5d': '效用值',
          'pain-scale': '疼痛等级'
        };
        
        this.scaleTrendChart.setOption({
          yAxis: {
            name: yName[this.selectedScale],
            max: yMax[this.selectedScale]
          },
          series: [
            {
              name: '平均得分',
              data: data[this.selectedScale]
            },
            {
              name: '正常参考值',
              data: Array(12).fill(refValue[this.selectedScale])
            }
          ]
        });
      }
    },
    
    // 更新科室填写情况对比图表
    updateDeptCompareChart() {
      this.initDeptCompareChart();
    },
    
    // 处理问卷选择变化
    handleQuestionnaireChange() {
      // 根据选择的问卷获取相应的问题列表
      if (this.selectedQuestionnaire) {
        this.questionOptions = [
          { id: 'q1_1', title: '您对医生的诊疗服务满意度如何？' },
          { id: 'q1_2', title: '您对护士的服务态度满意度如何？' },
          { id: 'q1_3', title: '您对医院环境和设施的满意度如何？' },
          { id: 'q1_4', title: '您对就诊等待时间的满意度如何？' },
          { id: 'q1_5', title: '您对检查/化验结果出具时间的满意度如何？' }
        ];
        this.selectedQuestion = 'q1_1';
        this.updateQuestionAnswerChart();
      } else {
        this.questionOptions = [];
        this.selectedQuestion = '';
      }
    },
    
    // 更新问题回答分析图表
    updateQuestionAnswerChart() {
      if (!this.questionAnswerChart || !this.selectedQuestion) return;
      
      // 这里只是示例，实际应该根据选择的问题获取相应的回答数据
      const answersData = {
        'q1_1': [
          { value: 60, name: '非常满意' },
          { value: 30, name: '满意' },
          { value: 8, name: '一般' },
          { value: 1.5, name: '不满意' },
          { value: 0.5, name: '非常不满意' }
        ],
        'q1_2': [
          { value: 65, name: '非常满意' },
          { value: 25, name: '满意' },
          { value: 7, name: '一般' },
          { value: 2, name: '不满意' },
          { value: 1, name: '非常不满意' }
        ],
        'q1_3': [
          { value: 50, name: '非常满意' },
          { value: 35, name: '满意' },
          { value: 10, name: '一般' },
          { value: 4, name: '不满意' },
          { value: 1, name: '非常不满意' }
        ],
        'q1_4': [
          { value: 40, name: '非常满意' },
          { value: 35, name: '满意' },
          { value: 15, name: '一般' },
          { value: 8, name: '不满意' },
          { value: 2, name: '非常不满意' }
        ],
        'q1_5': [
          { value: 45, name: '非常满意' },
          { value: 40, name: '满意' },
          { value: 12, name: '一般' },
          { value: 2, name: '不满意' },
          { value: 1, name: '非常不满意' }
        ]
      };
      
      const colors = ['#67C23A', '#85ce61', '#E6A23C', '#f89b9b', '#F56C6C'];
      
      this.questionAnswerChart.setOption({
        series: [{
          data: answersData[this.selectedQuestion].map((item, index) => {
            return {
              value: item.value,
              name: item.name,
              itemStyle: {
                color: colors[index]
              }
            };
          })
        }]
      });
    },
    
    // 处理搜索
    handleSearch() {
      this.$message.success('应用查询条件');
      this.fetchData();
      this.refreshCharts();
    },
    
    // 重置搜索
    resetSearch() {
      this.$refs.searchForm.resetFields();
      this.handleSearch();
    },
    
    // 刷新数据
    refreshData() {
      this.$message.success('数据已刷新');
      this.fetchData();
      this.refreshCharts();
    },
    
    // 刷新所有图表
    refreshCharts() {
      this.initSubmissionTrendChart();
      this.initSatisfactionDistChart();
      this.initScaleTrendChart();
      this.initDeptCompareChart();
      this.initQuestionAnswerChart();
    },
    
    // 重置所有图表大小
    resizeAllCharts() {
      this.submissionTrendChart && this.submissionTrendChart.resize();
      this.satisfactionDistChart && this.satisfactionDistChart.resize();
      this.scaleTrendChart && this.scaleTrendChart.resize();
      this.deptCompareChart && this.deptCompareChart.resize();
      this.questionAnswerChart && this.questionAnswerChart.resize();
    },
    
    // 销毁所有图表实例
    disposeAllCharts() {
      this.submissionTrendChart && this.submissionTrendChart.dispose();
      this.satisfactionDistChart && this.satisfactionDistChart.dispose();
      this.scaleTrendChart && this.scaleTrendChart.dispose();
      this.deptCompareChart && this.deptCompareChart.dispose();
      this.questionAnswerChart && this.questionAnswerChart.dispose();
    },
    
    // 导出数据
    exportData() {
      this.exportDialogVisible = true;
    },
    
    // 确认导出
    confirmExport() {
      const formatMap = {
        'excel': 'Excel',
        'pdf': 'PDF',
        'csv': 'CSV'
      };
      
      this.$message.success(`正在导出${formatMap[this.exportForm.format]}格式数据，请稍候...`);
      this.exportDialogVisible = false;
      
      // 模拟导出过程
      setTimeout(() => {
        this.$message({
          message: `数据导出完成，文件已保存到您的下载文件夹`,
          type: 'success',
          duration: 5000
        });
      }, 2000);
    },
    
    // 导出表格数据
    exportTableData() {
      this.$message.success('正在导出表格数据，请稍候...');
      
      // 模拟导出过程
      setTimeout(() => {
        this.$message({
          message: '表格数据导出完成，文件已保存到您的下载文件夹',
          type: 'success',
          duration: 3000
        });
      }, 1000);
    },
    
    // 分页大小变更
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.fetchData();
    },
    
    // 当前页变更
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.fetchData();
    },
    
    // 获取问卷类型文本
    getQuestionnaireTypeText(type) {
      const typeMap = {
        'satisfaction': '满意度问卷',
        'followup': '随访问卷',
        'assessment': '评估问卷'
      };
      return typeMap[type] || type;
    },
    
    // 获取问卷类型标签样式
    getQuestionnaireTypeTag(type) {
      const tagMap = {
        'satisfaction': 'success',
        'followup': 'primary',
        'assessment': 'warning'
      };
      return tagMap[type] || '';
    },
    
    // 查看详情
    viewDetails(row) {
      // 页面跳转或者弹出详情对话框
      this.$message.info(`查看问卷 "${row.questionnaireName}" 详情`);
    },
    
    // 分析详情
    analyzeDetail(row) {
      this.detailAnalysisVisible = true;
      
      // 模拟获取分析数据
      setTimeout(() => {
        this.currentAnalysis = {
          ...row,
          avgDuration: '3分42秒',
          keyIndicators: [
            { name: '完成率', value: row.completionRate, note: '高于平均水平5.2%' },
            { name: '平均满意度得分率', value: row.questionnaireType === 'satisfaction' ? Math.round(row.avgSatisfaction * 20) : 0, note: '较上期提升1.5%' },
            { name: '异常问题率', value: row.abnormalRate, note: '低于目标值(10%)' },
            { name: '按时提交率', value: 81.4, note: '低于平均水平3.1%' }
          ],
          questions: [
            {
              title: '您对医生的诊疗服务满意度如何？',
              type: '单选题',
              answerRate: 98.5,
              abnormalRate: 2.1,
              insight: '该问题满意度评分较高，95%的患者选择了"满意"或"非常满意"，表明医生的诊疗服务总体质量良好。相比去年同期，满意度提升了3.2个百分点。'
            },
            {
              title: '您对护士的服务态度满意度如何？',
              type: '单选题',
              answerRate: 99.2,
              abnormalRate: 1.5,
              insight: '护士服务态度获得了最高的满意度评分，96.8%的患者选择了"满意"或"非常满意"，是所有评价项目中表现最好的。'
            },
            {
              title: '您对医院环境和设施的满意度如何？',
              type: '单选题',
              answerRate: 97.8,
              abnormalRate: 4.2,
              insight: '医院环境和设施满意度略低于服务类问题，15%的患者选择了"一般"及以下评价，主要集中在"卫生间设施"和"等候区舒适度"两项。'
            },
            {
              title: '您对医院环境和设施的满意度如何？',
              type: '单选题',
              answerRate: 97.8,
              abnormalRate: 4.2,
              insight: '医院环境和设施满意度略低于服务类问题，15%的患者选择了"一般"及以下评价，主要集中在"卫生间设施"和"等候区舒适度"两项。'
            },
            {
              title: '您对就诊等待时间的满意度如何？',
              type: '单选题',
              answerRate: 95.6,
              abnormalRate: 8.3,
              insight: '等待时间是满意度较低的问题，25%的患者选择了"一般"或"不满意"，建议优化挂号和诊疗流程，缩短患者等待时间。'
            },
            {
              title: '您对检查/化验结果出具时间的满意度如何？',
              type: '单选题',
              answerRate: 96.2,
              abnormalRate: 6.5,
              insight: '18%的患者对结果出具时间选择了"一般"及以下评价，相比上季度有3.2%的改善，但仍需进一步优化流程。'
            }
          ],
          trendInsight: '本期问卷整体满意度与上期相比提升了2.3个百分点，其中医生诊疗服务满意度提升最明显，环境设施满意度有小幅下降。相比去年同期，整体满意度提升了5.6%，说明医院服务质量持续改善。',
          groupInsight: '不同年龄段患者满意度存在差异，60岁以上老年患者满意度最高(93.2%)，18-30岁青年患者满意度最低(82.5%)。地区差异方面，本地患者满意度(89.6%)高于外地患者(84.3%)。',
          aiSummary: '本期满意度调查总体表现良好，完成率86.4%，高于行业平均水平。医护人员服务满意度突出，环境设施和等待时间仍有改进空间。建议重点关注年轻患者群体需求，优化挂号和检查流程，改善就诊体验。',
          aiInsights: [
            {
              title: '医护服务是满意度亮点',
              content: '护士服务态度获得96.8%的高满意度，医生诊疗服务获得95%的满意度，均高于行业标准。患者反馈中"专业""耐心""细致"等正面评价词频较高。'
            },
            {
              title: '等待时间是主要痛点',
              content: '25%的患者对等待时间表示不满，其中挂号等待和检查结果出具时间是两个主要问题点。通过数据分析，上午9-10点和下午3-4点是就诊高峰期，等待时间显著增加。'
            },
            {
              title: '年龄段差异明显',
              content: '18-30岁患者满意度最低(82.5%)，主要对等待时间和环境设施不满；60岁以上患者满意度最高(93.2%)，对医护服务评价特别高。这表明医院服务可能更适合老年患者需求。'
            }
          ],
          aiRecommendations: [
            {
              content: '增加高峰期人力配置，优化分诊流程，缩短患者等待时间',
              type: 'primary',
              priority: 'high'
            },
            {
              content: '改进移动端预约和结果查询系统，满足年轻患者对信息化服务的需求',
              type: 'success',
              priority: 'medium'
            },
            {
              content: '增加休息区座位和充电设施，改善就诊环境',
              type: 'info',
              priority: 'medium'
            },
            {
              content: '开展针对性的医护培训，保持和提升服务质量',
              type: 'warning',
              priority: 'low'
            }
          ]
        };
        
        // 初始化问题图表
        this.$nextTick(() => {
          this.initDetailCharts();
        });
      }, 500);
    },
    
    // 初始化详情页内的图表
    initDetailCharts() {
      if (!this.currentAnalysis) return;
      
      // 初始化问题图表
      this.currentAnalysis.questions.forEach((question, index) => {
        const chartDom = document.getElementById(`questionChart-${index}`);
        if (!chartDom) return;
        
        const chart = echarts.init(chartDom);
        let option = {
          tooltip: {
            trigger: 'item'
          },
          legend: {
            orient: 'horizontal',
            bottom: 0
          },
          series: [
            {
              name: '回答分布',
              type: 'pie',
              radius: '60%',
              data: [
                { value: 55, name: '非常满意', itemStyle: { color: '#67C23A' } },
                { value: 40 - index * 3, name: '满意', itemStyle: { color: '#85ce61' } },
                { value: 5 + index * 2, name: '一般', itemStyle: { color: '#E6A23C' } },
                { value: index, name: '不满意', itemStyle: { color: '#f89b9b' } },
                { value: index > 0 ? 1 : 0, name: '非常不满意', itemStyle: { color: '#F56C6C' } }
              ]
            }
          ]
        };
        chart.setOption(option);
      });
      
      // 初始化趋势图表
      const trendChartDom = document.getElementById('detailTrendChart');
      if (trendChartDom) {
        const trendChart = echarts.init(trendChartDom);
        const option = {
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: ['本期', '上期', '去年同期']
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
          },
          yAxis: {
            type: 'value',
            name: '满意度得分',
            min: 70,
            max: 100
          },
          series: [
            {
              name: '本期',
              type: 'line',
              data: [84, 86, 88, 87, 85, 86, 88, 89, 90, 92, 91, 93],
              lineStyle: { width: 3 },
              itemStyle: { color: '#409EFF' }
            },
            {
              name: '上期',
              type: 'line',
              data: [82, 83, 85, 84, 82, 83, 85, 86, 87, 89, 88, 90],
              lineStyle: { width: 2 },
              itemStyle: { color: '#909399' }
            },
            {
              name: '去年同期',
              type: 'line',
              data: [78, 80, 81, 82, 80, 81, 83, 84, 85, 86, 85, 87],
              lineStyle: { width: 2, type: 'dashed' },
              itemStyle: { color: '#C0C4CC' }
            }
          ]
        };
        trendChart.setOption(option);
      }
      
      // 初始化分群比较图表
      this.updateGroupAnalysis();
    },
    
    // 更新分群分析图表
    updateGroupAnalysis() {
      const chartDom = document.getElementById('groupAnalysisChart');
      if (!chartDom) return;
      
      const chart = echarts.init(chartDom);
      
      let xAxisData = [];
      let seriesData = [];
      
      switch(this.groupByFactor) {
        case 'age':
          xAxisData = ['18岁以下', '18-30岁', '31-45岁', '46-60岁', '60岁以上'];
          seriesData = [86.5, 82.5, 87.8, 89.4, 93.2];
          break;
        case 'gender':
          xAxisData = ['男性', '女性'];
          seriesData = [87.3, 88.6];
          break;
        case 'disease':
          xAxisData = ['心血管疾病', '消化系统疾病', '呼吸系统疾病', '内分泌疾病', '骨科疾病', '其他'];
          seriesData = [90.2, 87.5, 85.6, 88.9, 86.3, 87.2];
          break;
        case 'area':
          xAxisData = ['本市', '本省其他地区', '外省'];
          seriesData = [89.6, 86.2, 84.3];
          break;
      }
      
      const option = {
        tooltip: {
          trigger: 'axis',
          formatter: '{b}: {c}%'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xAxisData
        },
        yAxis: {
          type: 'value',
          name: '满意度(%)',
          min: 80,
          max: 100
        },
        series: [{
          data: seriesData.map(value => {
            return {
              value: value,
              itemStyle: {
                color: value > 88 ? '#67C23A' : value > 85 ? '#409EFF' : '#E6A23C'
              }
            };
          }),
          type: 'bar',
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(180, 180, 180, 0.2)'
          },
          label: {
            show: true,
            position: 'top',
            formatter: '{c}%'
          }
        }]
      };
      
      chart.setOption(option);
    },
    
    // 获取进度条颜色
    getProgressColor(value) {
      if (value >= 90) return '#67C23A';
      if (value >= 80) return '#409EFF';
      if (value >= 70) return '#E6A23C';
      return '#F56C6C';
    },
    
    // 获取推荐优先级文本
    getPriorityText(priority) {
      const texts = {
        'high': '高优先级',
        'medium': '中优先级',
        'low': '低优先级'
      };
      return texts[priority] || '';
    },
    
    // 获取推荐颜色
    getRecommendationColor(priority) {
      const colors = {
        'high': '#F56C6C',
        'medium': '#E6A23C',
        'low': '#67C23A'
      };
      return colors[priority] || '#409EFF';
    },
    
    // 导出分析报告
    exportAnalysisReport() {
      if (!this.currentAnalysis) return;
      
      this.$message.success('正在导出分析报告，请稍候...');
      
      // 模拟导出过程
      setTimeout(() => {
        this.$message({
          message: `问卷 "${this.currentAnalysis.questionnaireName}" 的分析报告已导出`,
          type: 'success',
          duration: 3000
        });
      }, 1500);
    },
    
    // 更新分组比较
    updateGroupByFactor() {
      this.updateGroupAnalysis();
    }
  }
}
</script>

<style scoped>
.questionnaire-analyze-container {
  padding: 20px 0;
}

/* 卡片通用样式 */
.page-header-card, .search-card, .chart-card, .table-card {
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

/* 页面标题样式 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-left h2 {
  margin: 0 10px 0 0;
}

/* 数据概览样式 */
.data-overview {
  margin-top: 20px;
}

.overview-card {
  height: 120px;
}

.overview-item {
  display: flex;
  align-items: center;
  height: 100%;
}

.overview-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-right: 15px;
  color: white;
}

.overview-icon i {
  font-size: 30px;
}

.blue {
  background-color: #409EFF;
}

.green {
  background-color: #67C23A;
}

.orange {
  background-color: #E6A23C;
}

.red {
  background-color: #F56C6C;
}

.overview-content {
  flex: 1;
}

.overview-title {
  font-size: 16px;
  color: #606266;
  margin-bottom: 8px;
}

.overview-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}

.overview-trend {
  font-size: 12px;
  color: #909399;
}

.up {
  color: #67C23A;
  margin-right: 5px;
}

.down {
  color: #F56C6C;
  margin-right: 5px;
}

/* 图表样式 */
.chart-card {
  height: 400px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chart-container {
  width: 100%;
  height: 340px;
}

.header-controls {
  display: flex;
}

/* 表格区域样式 */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-container {
  margin-top: 20px;
  text-align: center;
}

.text-danger {
  color: #F56C6C;
}

.text-warning {
  color: #E6A23C;
}

/* 表单提示 */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

/* 详细分析对话框样式 */
.detail-analysis {
  padding: 10px 0;
}

.analysis-overview {
  margin-bottom: 20px;
}

.analysis-indicators {
  margin-top: 20px;
}

.indicator-items {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 15px;
}

.indicator-item {
  flex: 1;
  min-width: 200px;
  max-width: 45%;
}

.indicator-title {
  font-weight: bold;
  margin-bottom: 10px;
  color: #303133;
}

.indicator-note {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.question-analysis {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #EBEEF5;
}

.question-header {
  margin-bottom: 15px;
}

.question-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 8px;
  color: #303133;
}

.question-meta {
  display: flex;
  gap: 15px;
  font-size: 13px;
  color: #909399;
}

.question-chart {
  height: 260px;
  margin-bottom: 15px;
}

.question-insights {
  background-color: #F8F8F8;
  padding: 12px 15px;
  border-radius: 4px;
  border-left: 3px solid #409EFF;
}

.question-insights h5 {
  margin-top: 0;
  margin-bottom: 8px;
  color: #303133;
}

.question-insights p {
  margin: 0;
  color: #606266;
  line-height: 1.6;
}

.trend-analysis, .group-analysis, .ai-analysis {
  padding: 10px 0;
}

.trend-chart, #groupAnalysisChart {
  height: 350px;
  margin-bottom: 20px;
}

.trend-insights, .group-insights {
  background-color: #F8F8F8;
  padding: 12px 15px;
  border-radius: 4px;
  border-left: 3px solid #409EFF;
}

.trend-insights h5, .group-insights h5 {
  margin-top: 0;
  margin-bottom: 8px;
  color: #303133;
}

.trend-insights p, .group-insights p {
  margin: 0;
  color: #606266;
  line-height: 1.6;
}

.filter-panel {
  background-color: #F5F7FA;
  padding: 15px;
  border-radius: 4px;
  height: 100%;
}

.filter-panel h5 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #303133;
}

.ai-analysis {
  padding: 10px 0;
}

.ai-summary, .ai-insights, .ai-recommendations {
  margin-top: 20px;
}

.ai-summary h4, .ai-insights h4, .ai-recommendations h4 {
  color: #303133;
  margin-bottom: 15px;
}

.ai-summary p {
  line-height: 1.6;
  color: #606266;
}
</style>